import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BCHeader from "../../shared/BCHeader";
import { Input, Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { SwitchButton } from "../../components/shared/SwitchButton";
import { Box, HStack, VStack } from "../../components/utils";
import {
  useCreateQuickEsopPool,
  useGetOnGoingTransaction,
  usePostOnGoingTransaction,
} from "../../queries/transactionRound";
import { useAuthStore } from "../../store";
import { useQuickEsopPoolUpdateTransactionStore } from "../../store/useQuickTransactionStore";
import TransactionPreviewPage from "./TransactionPreviewPage";
import {
  ButtonSize,
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../quickRound/CTAButtonComponents";

type UpdateMode = "shares" | "percentage";

function EsopPoolUpdatePage() {
  const navigate = useNavigate();
  const companyId = useAuthStore.getState().companyId || "";
  const { state: template, setState: setEsopPoolUpdateTemplate } =
    useQuickEsopPoolUpdateTransactionStore();
  const [mode, setMode] = useState<UpdateMode>("shares");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [shares, setShares] = useState("");
  const [percentage, setPercentage] = useState("");
  const [decreasePool, setDecreasePool] = useState(false);
  const { mutate: create, isLoading: isCreating } = useCreateQuickEsopPool();
  const { mutate: postOnGoingTransaction } = usePostOnGoingTransaction();
  const { data: onGoingTransaction, refetch: refetchOnGoingTransaction } =
    useGetOnGoingTransaction(companyId);

  useEffect(() => {
    refetchOnGoingTransaction();
  }, []);

  useEffect(() => {
    if (onGoingTransaction?.transactionType?.toLowerCase() === "esoppool") {
      setEsopPoolUpdateTemplate(onGoingTransaction);
    }
  }, [onGoingTransaction]);

  useEffect(() => {
    if (template.stepNo === 1 && template.date) {
      setDate(template.date);
      setMode(template.updateMode || "shares");
      setShares(template.shares || "");
      setPercentage(template.percentage || "");
      setDecreasePool(template.decreasePool || false);
    }
  }, [template.stepNo]);

  const createEvent = () => {
    const amount = Number(mode === "shares" ? shares : percentage);
    const validAmount = Number.isFinite(amount) && amount > 0;
    const validShares = mode !== "shares" || Number.isInteger(amount);
    const validPercentage = mode !== "percentage" || amount < 100;

    if (!date || !validAmount || !validShares || !validPercentage) {
      toast(
        mode === "shares"
          ? "Enter a positive whole number of shares"
          : "Enter a percentage between 0 and 100",
        { type: "error" }
      );
      return;
    }

    create(
      {
        companyId,
        date: new Date(date).toISOString(),
        poolPercentage: mode === "percentage" ? amount / 100 : null,
        numberOfShares: mode === "shares" ? amount : null,
        isPoolPercentageGiven: mode === "percentage",
        decreasePool: mode === "shares" && decreasePool,
      },
      {
        onSuccess: (event) => {
          const poolUpdateTransaction = {
            eventId: event.id,
            stepNo: 2,
            transactionType: "esopPool" as const,
            companyId,
            date,
            updateMode: mode,
            shares,
            percentage,
            decreasePool,
          };
          postOnGoingTransaction(poolUpdateTransaction);
          setEsopPoolUpdateTemplate(poolUpdateTransaction);
        },
        onError: (error: any) => {
          toast(
            error?.response?.data?.errorMessage ||
              error?.message ||
              "Something went wrong",
            { type: "error" }
          );
        },
      }
    );
  };

  if (template.stepNo === 2) {
    return (
      <div className="min-h-full">
        <BCHeader
          className="items-baseline mb-2"
          bcTitle="Transaction Dashboard"
          bcSubTitle="ESOP Pool Update"
        />
        <TransactionPreviewPage />
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <BCHeader
        className="items-baseline mb-2"
        bcTitle="Transaction Dashboard"
        bcSubTitle="ESOP Pool Update"
      />
      <Box className="min-h-[430px] bg-white rounded-lg p-6">
        <VStack className="gap-8">
          <VStack className="gap-1">
            <p className="text-xl font-semibold text-gray-dark">
              ESOP Pool Update
            </p>
            <p className="text-xs font-medium text-gray-light">
              Update the option pool, then review and approve the event.
            </p>
          </VStack>
          <HStack className="justify-start gap-10">
            <VStack>
              <Label className="py-2 text-sm font-medium">Effective date</Label>
              <Input
                className="w-60"
                type="date"
                value={date}
                onChange={(event: any) => setDate(event.target.value)}
              />
            </VStack>
            <VStack>
              <Label className="py-2 text-sm font-medium">Update by</Label>
              <Select
                className="w-60"
                options={["shares", "percentage"]}
                value={mode}
                onChange={(event) => setMode(event.target.value as UpdateMode)}
                textGetter={(option) =>
                  option === "shares"
                    ? "Number of shares"
                    : "Target pool percentage"
                }
              />
            </VStack>
            {mode === "shares" ? (
              <>
                <VStack>
                  <Label className="py-2 text-sm font-medium whitespace-nowrap">
                    Number of shares
                  </Label>
                  <Input
                    className="w-60"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Enter shares"
                    value={shares}
                    onChange={(event: any) => setShares(event.target.value)}
                  />
                </VStack>
                <VStack>
                  <Label className="py-2 text-sm font-medium whitespace-nowrap">
                    Decrease pool
                  </Label>
                  <SwitchButton
                    className="text-sm font-medium items-center ml-2 mb-1"
                    label=""
                    value={decreasePool}
                    onClick={() => setDecreasePool(!decreasePool)}
                  />
                </VStack>
              </>
            ) : (
              <VStack className="w-1/3">
                <Label className="py-2 text-sm font-medium">
                  Target pool percentage
                </Label>
                <Input
                  className="w-60"
                  type="number"
                  min="0.01"
                  max="99.99"
                  step="0.01"
                  placeholder="Enter percentage"
                  value={percentage}
                  onChange={(event: any) => setPercentage(event.target.value)}
                />
              </VStack>
            )}
          </HStack>
          <HStack className="justify-between gap-3 pt-4">
            <SecondaryCTAButton
              buttonSize={ButtonSize.SMALL}
              onClick={() => navigate("/transactions/allTransactions")}
            >
              Cancel
            </SecondaryCTAButton>
            <PrimaryCTAButton
              buttonSize={ButtonSize.SMALL}
              loading={isCreating}
              onClick={createEvent}
            >
              Preview event
            </PrimaryCTAButton>
          </HStack>
        </VStack>
      </Box>
    </div>
  );
}

export default EsopPoolUpdatePage;
